/**
 * The duplicate-login warning (`q-duplicate-login-warning`; networking.md "One live game session
 * per account"). Shown when the server answers a hello `alreadyLoggedIn`: this account is
 * already playing on another device. Two choices, and nothing has entered the world yet:
 *
 * - **Back out** (the default, focused first; Escape chooses it): the other device keeps playing.
 * - **End the other session and play here**: the server ends the other session cleanly and
 *   admits this one; the other device is told and returns to its login screen.
 *
 * Keyboard: focus starts on "Back out", Tab / Shift+Tab cycle between the two buttons (focus is
 * held inside the dialog while it is open), Enter/Space activate, Escape backs out.
 */

import { useEffect, useRef, type KeyboardEvent } from 'react';

import { duplicateLoginCopy } from './duplicateLoginCopy.js';

export function DuplicateLoginDialog({
  busy,
  rateLimited,
  onBackOut,
  onTakeOver,
}: {
  /** The takeover is in flight: both buttons wait. */
  busy: boolean;
  /** The last takeover was refused as too soon after another device change. */
  rateLimited: boolean;
  onBackOut: () => void;
  onTakeOver: () => void;
}) {
  const backOutRef = useRef<HTMLButtonElement | null>(null);
  const takeOverRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    backOutRef.current?.focus();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!busy) onBackOut();
      return;
    }
    if (e.key !== 'Tab') return;
    // Hold focus inside the dialog: the two buttons are its only stops.
    const stops = [backOutRef.current, takeOverRef.current].filter(
      (b): b is HTMLButtonElement => b !== null,
    );
    if (stops.length === 0) return;
    const at = stops.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.shiftKey
      ? stops[(at <= 0 ? stops.length : at) - 1]
      : stops[(at + 1) % stops.length];
    e.preventDefault();
    next?.focus();
  };

  const copy = duplicateLoginCopy(rateLimited);
  return (
    <div className="duplicate-login-backdrop">
      <div
        className="duplicate-login panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="duplicate-login-title"
        aria-describedby="duplicate-login-body"
        onKeyDown={onKeyDown}
      >
        <h1 id="duplicate-login-title">{copy.title}</h1>
        <p id="duplicate-login-body" className="sub">
          {copy.body}
        </p>
        {copy.note ? (
          <p className="error" role="status">
            {copy.note}
          </p>
        ) : null}
        <button ref={backOutRef} type="button" disabled={busy} onClick={onBackOut}>
          {copy.backOut}
        </button>
        <button
          ref={takeOverRef}
          type="button"
          className="secondary"
          disabled={busy}
          onClick={onTakeOver}
        >
          {busy ? copy.takingOver : copy.takeOver}
        </button>
      </div>
    </div>
  );
}
